import axios from 'axios';

// Configuration
const AI_SERVICE_CONFIG = {
  // For development, use mock data
  useMockData: process.env.NODE_ENV === 'development' || !process.env.REACT_APP_OPENAI_API_KEY,
  
  
  // API endpoints
  endpoints: {
    openai: 'https://api.openai.com/v1/chat/completions',
  },
  
  // Default models
  models: {
    analysis: 'gpt-3.5-turbo',
    curriculum: 'gpt-3.5-turbo',
    guide: 'gpt-3.5-turbo',
    prediction: 'gpt-3.5-turbo'
  }
};

class AILearningService {
  constructor() {
    this.useMockData = AI_SERVICE_CONFIG.useMockData;
    this.openAIKey = AI_SERVICE_CONFIG.openAIKey;
  }

  // ============ CORE AI FUNCTIONS ============

  // 1. Enhanced AI Prediction (Replaces your current mock version)
  async generateAiPrediction(childData, teacherObservations = [], performanceHistory = []) {
    if (this.useMockData || !this.useOpenAI) {
      return this.getMockPrediction(childData);
    }

    try {
      const prompt = this.createPredictionPrompt(childData, teacherObservations, performanceHistory);
      const response = await this.callOpenAI(prompt, 'prediction');
      return this.parsePredictionResponse(response);
    } catch (error) {
      console.error('AI Prediction Error:', error);
      return this.getMockPrediction(childData);
    }
  }

  // 2. Enhanced Study Plan Generation
  async generateStudyPlan(childData, subject, goals = [], timeAvailable = '5 hours per week') {
    if (this.useMockData || !this.useOpenAI) {
      return this.getMockStudyPlan(childData, subject);
    }

    try {
      const prompt = this.createStudyPlanPrompt(childData, subject, goals, timeAvailable);
      const response = await this.callOpenAI(prompt, 'study_plan');
      return this.parseStudyPlanResponse(response);
    } catch (error) {
      console.error('AI Study Plan Error:', error);
      return this.getMockStudyPlan(childData, subject);
    }
  }

  // 3. Analyze Learning Behavior (NEW)
  async analyzeLearningBehavior(childData, observations = [], performanceHistory = []) {
    if (this.useMockData || !this.useOpenAI) {
      return this.getMockAnalysis(childData);
    }

    try {
      const prompt = this.createLearningAnalysisPrompt(childData, observations, performanceHistory);
      const response = await this.callOpenAI(prompt, 'learning_analysis');
      return this.parseAnalysisResponse(response);
    } catch (error) {
      console.error('AI Analysis Error:', error);
      return this.getMockAnalysis(childData);
    }
  }

  // 4. Generate Personalized Curriculum (NEW)
  async generatePersonalizedCurriculum(childData, analysis, subject, duration = '4 weeks') {
    if (this.useMockData || !this.useOpenAI) {
      return this.getMockCurriculum(childData, subject);
    }

    try {
      const prompt = this.createCurriculumPrompt(childData, analysis, subject, duration);
      const response = await this.callOpenAI(prompt, 'curriculum');
      return this.parseCurriculumResponse(response);
    } catch (error) {
      console.error('AI Curriculum Error:', error);
      return this.getMockCurriculum(childData, subject);
    }
  }

  // 5. Create Learning Guide for Student (NEW)
  async createLearningGuide(childName, analysis, strengths, weaknesses) {
    if (this.useMockData || !this.useOpenAI) {
      return this.getMockLearningGuide(childName);
    }

    try {
      const prompt = this.createLearningGuidePrompt(childName, analysis, strengths, weaknesses);
      const response = await this.callOpenAI(prompt, 'learning_guide');
      return this.parseLearningGuideResponse(response);
    } catch (error) {
      console.error('AI Learning Guide Error:', error);
      return this.getMockLearningGuide(childName);
    }
  }

  // 6. Generate Lesson Plan for Teacher (NEW)
  async generateLessonPlan(childData, subject, learningObjectives, duration = '60 minutes') {
    if (this.useMockData || !this.useOpenAI) {
      return this.getMockLessonPlan(childData, subject);
    }

    try {
      const prompt = this.createLessonPlanPrompt(childData, subject, learningObjectives, duration);
      const response = await this.callOpenAI(prompt, 'lesson_plan');
      return this.parseLessonPlanResponse(response);
    } catch (error) {
      console.error('AI Lesson Plan Error:', error);
      return this.getMockLessonPlan(childData, subject);
    }
  }

  // ============ OPENAI API CALL ============

  async callOpenAI(prompt, context) {
  try {
    const response = await axios.post('/api/ai/process', {
      prompt,
      context
    });

    return response.data;
  } catch (error) {
    console.error('Backend AI call failed:', error);
    throw error;
  }
}

  // ============ PROMPT TEMPLATES ============

  getSystemPrompt(context) {
    const prompts = {
      prediction: "You are an expert educational psychologist specializing in student performance prediction. Analyze the data and provide accurate predictions with actionable recommendations.",
      study_plan: "You are a professional study planner specializing in creating effective, manageable study schedules for students.",
      learning_analysis: "You are an expert educational analyst who identifies learning patterns, strengths, weaknesses, and provides specific recommendations.",
      curriculum: "You are a curriculum development expert specializing in creating personalized learning paths for individual students.",
      learning_guide: "You are a learning coach who creates engaging, student-friendly learning guides that motivate and guide students.",
      lesson_plan: "You are an experienced teacher who creates detailed lesson plans tailored to individual student needs and learning styles."
    };
    return prompts[context] || "You are an educational expert.";
  }

  createPredictionPrompt(childData, observations, performanceHistory) {
    return `Analyze this student's learning data and predict future performance:

STUDENT PROFILE:
- Name: ${childData.firstName} ${childData.lastName}
- Age: ${childData.age || 'N/A'}
- Grade: ${childData.grade || 'N/A'}
- Learning Style: ${childData.learningStyle || 'Unknown'}
- Subjects: ${childData.subjects?.join(', ') || 'Not specified'}

TEACHER OBSERVATIONS (${observations.length} observations):
${observations.map((obs, i) => `${i + 1}. ${obs.comment || obs.note} (${obs.date?.toDate?.().toLocaleDateString() || obs.timestamp || 'N/A'})`).join('\n')}

PERFORMANCE HISTORY (${performanceHistory.length} records):
${performanceHistory.map((perf, i) => `${i + 1}. ${perf.subject}: ${perf.score}% (${perf.date?.toDate?.().toLocaleDateString() || 'N/A'}) - ${perf.assessmentType || 'Test'}`).join('\n')}

Provide your analysis in this JSON format:
{
  "predictedPerformance": "Excellent/Good/Average/Needs Improvement",
  "confidenceScore": 85,
  "strengths": ["strength1", "strength2", "strength3"],
  "weaknesses": ["weakness1", "weakness2", "weakness3"],
  "recommendations": ["recommendation1", "recommendation2", "recommendation3"],
  "predictedGradeImprovement": "+15% over 3 months",
  "keyFocusAreas": ["area1", "area2"],
  "learningStyleAnalysis": "Visual/Auditory/Kinesthetic",
  "studyHourRecommendation": "hours per week"
}`;
  }

  createStudyPlanPrompt(childData, subject, goals, timeAvailable) {
    return `Create a personalized study plan for this student:

STUDENT:
- Name: ${childData.firstName} ${childData.lastName}
- Grade: ${childData.grade || 'N/A'}
- Previous Performance: ${childData.performance?.overallScore || 'Unknown'}%

SUBJECT: ${subject}
TIME AVAILABLE: ${timeAvailable}
GOALS: ${goals.length > 0 ? goals.join(', ') : 'Improve overall performance'}

Create a comprehensive study plan including:
1. Weekly schedule breakdown
2. Specific learning objectives
3. Recommended resources
4. Assessment strategy
5. Progress tracking methods

Format as JSON with:
- weeklySchedule (array of day plans)
- learningObjectives
- requiredResources
- assessmentPlan
- milestones
- successCriteria`;
  }

  createLearningAnalysisPrompt(childData, observations, performanceHistory) {
    return `Analyze this student's learning behavior and patterns:

STUDENT:
- Name: ${childData.firstName} ${childData.lastName}
- Grade: ${childData.grade || 'N/A'}
- Learning Preferences: ${childData.learningPreferences || 'Not specified'}

OBSERVATIONS:
${observations.slice(0, 10).map((obs, i) => `${i + 1}. ${obs.comment} (Rating: ${obs.rating}/5, Date: ${obs.date?.toDate?.().toLocaleDateString() || 'N/A'})`).join('\n')}

PERFORMANCE TREND:
${performanceHistory.slice(0, 10).map(p => `- ${p.subject}: ${p.score}%`).join('\n')}

Provide a detailed learning behavior analysis in JSON format including:
- learningPatterns
- engagementLevel
- motivationFactors
- attentionSpanAnalysis
- recommendedTeachingStrategies
- estimatedImprovementTimeline`;
  }

  // ... (other prompt creation methods)

  // ============ RESPONSE PARSING ============

  parsePredictionResponse(response) {
    try {
      const content = response.choices[0].message.content;
      const parsed = JSON.parse(content);
      return {
        ...parsed,
        generatedAt: new Date().toISOString(),
        source: 'openai'
      };
    } catch (error) {
      console.error('Failed to parse prediction response:', error);
      return this.getMockPrediction({});
    }
  }

  // ============ MOCK DATA GENERATORS (for development) ============

  getMockPrediction(childData) {
    const subjects = ['Mathematics', 'English', 'Science', 'Social Studies'];
    const strengths = ['Logical Reasoning', 'Memory Retention', 'Problem Solving', 'Critical Thinking'];
    const weaknesses = ['Time Management', 'Reading Comprehension', 'Note Taking', 'Focus'];
    
    return {
      predictedPerformance: 'Good',
      confidenceScore: 82,
      strengths: this.getRandomItems(strengths, 3),
      weaknesses: this.getRandomItems(weaknesses, 2),
      recommendations: [
        'Increase practice sessions in identified weak areas',
        'Implement structured study schedule',
        'Use visual aids for complex concepts',
        'Regular progress assessments every 2 weeks'
      ],
      predictedGradeImprovement: '+18% over 3 months',
      keyFocusAreas: this.getRandomItems(subjects, 2),
      learningStyleAnalysis: 'Visual Learner',
      studyHourRecommendation: '8 hours per week',
      generatedAt: new Date().toISOString(),
      source: 'mock'
    };
  }

  getMockStudyPlan(childData, subject) {
    return {
      weeklySchedule: [
        {
          day: 'Monday',
          activities: ['Concept Introduction', 'Basic Practice'],
          duration: '1 hour',
          resources: ['Textbook Chapter 1', 'Online Videos']
        },
        {
          day: 'Wednesday',
          activities: ['Advanced Problems', 'Group Study'],
          duration: '1.5 hours',
          resources: ['Practice Worksheets', 'Interactive Exercises']
        },
        {
          day: 'Friday',
          activities: ['Review Session', 'Mock Test'],
          duration: '1 hour',
          resources: ['Review Notes', 'Test Papers']
        }
      ],
      learningObjectives: [
        'Master core concepts of ' + subject,
        'Improve problem-solving speed by 30%',
        'Achieve 85%+ in weekly assessments'
      ],
      requiredResources: [
        subject + ' Textbook',
        'Online Practice Platform',
        'Study Notebook',
        'Calculator (if needed)'
      ],
      assessmentPlan: [
        'Weekly quizzes every Friday',
        'Monthly progress tests',
        'Practical assignments'
      ],
      milestones: [
        'Week 1: Complete foundation',
        'Week 2-3: Practice and reinforcement',
        'Week 4: Final assessment'
      ],
      successCriteria: [
        'Score 85% on weekly quizzes',
        'Complete all assignments on time',
        'Demonstrate concept mastery'
      ],
      generatedAt: new Date().toISOString(),
      source: 'mock'
    };
  }

  // Helper method
  getRandomItems(array, count) {
    const shuffled = [...array].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }
}

// Create and export singleton instance
export const aiLearningService = new AILearningService();